import { ProjectDTO, ProjectPeriodicDataAttributeType, ProjectQuarterlyDataAttribute } from 'api';
import { ProjectStatus, ReportStatus } from 'api/enumTypes';
import { parseISO } from 'date-fns';
import { addQuarters } from 'date-fns/addQuarters';
import { getQuarter } from 'date-fns/getQuarter';
import { getYear } from 'date-fns/getYear';
import { parse as parseDate } from 'date-fns/parse';
import { getAttribute, getAttributeValue } from 'lib/context/project-context';
import useSettings from 'lib/context/settings-context';
import { formatDateTime } from 'lib/utils/dateFormat';
import _ from 'lodash';

/**
 * Not to be confused with ReportStatus
 *
 * This status represents the status for the entire project.
 */
export enum ReportFormStatus {
	NOT_DELIVERED = 'Ikke levert',
	SUBMITTED = 'Levert',
	RETURNED = 'Returnert',
	APPROVED = 'Fullført',
}

export function useQuarterlyDeadlines() {
	const { systemSettings } = useSettings();
	const q1 =
		systemSettings.find((setting) => setting.attributeType === 'QUARTERLY_REPORT_DEADLINE' && setting.quarter === 1)
			?.value ?? '30.04';
	const q2 =
		systemSettings.find((setting) => setting.attributeType === 'QUARTERLY_REPORT_DEADLINE' && setting.quarter === 2)
			?.value ?? '31.08';
	const q3 =
		systemSettings.find((setting) => setting.attributeType === 'QUARTERLY_REPORT_DEADLINE' && setting.quarter === 3)
			?.value ?? '31.10';
	const q4 =
		systemSettings.find((setting) => setting.attributeType === 'QUARTERLY_REPORT_DEADLINE' && setting.quarter === 4)
			?.value ?? '31.01';

	return [
		{ quarter: 1, deadline: q1 },
		{ quarter: 2, deadline: q2 },
		{ quarter: 3, deadline: q3 },
		{ quarter: 4, deadline: q4 },
	];
}

export function getLatestReportStatus(
	reportStatuses: ProjectQuarterlyDataAttribute[],
	filter?: (attr: ProjectQuarterlyDataAttribute) => boolean
) {
	// apply filter if it is passed
	reportStatuses = filter ? reportStatuses.filter(filter) : reportStatuses;

	let latestReportStatus = reportStatuses[0];
	for (const current of reportStatuses) {
		latestReportStatus = isAfter(current, latestReportStatus) ? current : latestReportStatus;
	}
	return latestReportStatus;
}

export function getReportFormStatus(reportStatuses: ProjectQuarterlyDataAttribute[], today: Date): ReportFormStatus {
	if (reportStatuses.length === 0) {
		return ReportFormStatus.NOT_DELIVERED;
	}
	const latestReportStatus = getLatestReportStatus(reportStatuses);
	const quarterAfterLastReport = addQuarter(latestReportStatus);
	const haveFreeQuarter = isAfter({ year: getYear(today), quarter: getQuarter(today) }, quarterAfterLastReport);
	const value = latestReportStatus.value as ReportStatus;

	if (haveFreeQuarter && value === 'APPROVED') {
		return ReportFormStatus.NOT_DELIVERED;
	} else if (value === 'SUBMITTED') {
		return ReportFormStatus.SUBMITTED;
	} else if (value === 'RETURNED') {
		return ReportFormStatus.RETURNED;
	} else if (value === 'DRAFT') {
		return ReportFormStatus.NOT_DELIVERED;
	} else {
		return ReportFormStatus.APPROVED;
	}
}

export function getEarliestReportedYear(
	reportStatuses: ProjectQuarterlyDataAttribute[],
	projectDTO: ProjectDTO
): number {
	const isNewProject: boolean = reportStatuses.length === 0;
	if (isNewProject) {
		return getNewProjectQuarters(projectDTO).year;
	} else {
		let earliestYear = new Date().getFullYear();
		reportStatuses.forEach((status) => {
			if (status.year < earliestYear) {
				earliestYear = status.year;
			}
		});
		return earliestYear;
	}
}

type YearAndQuarter =
	| ProjectQuarterlyDataAttribute
	| {
			year: number;
			quarter: number;
	  };

/**
 * comparing year and quarter, returns true if a is after b
 */
function isAfter(a: YearAndQuarter, b: YearAndQuarter) {
	if (a.year > b.year) {
		return true;
	}
	return a.year === b.year && a.quarter > b.quarter;
}

/////////////////////////////////////////////////////////////

function addQuarter(current: YearAndQuarter): YearAndQuarter {
	let newDate = parseDate(`${current.year} ${current.quarter}`, 'yyyy Q', new Date());
	newDate = addQuarters(newDate, 1);
	return { year: getYear(newDate), quarter: getQuarter(newDate) };
}

function getNewProjectQuarters(projectDTO: ProjectDTO): YearAndQuarter {
	const projectStart = getAttribute(projectDTO, 'STATUS');
	const lastModified = projectStart?.lastModified;
	const startOfReportingNewProject = new Date(lastModified!);
	//return { year: getYear(startOfReportingNewProject), quarter: getQuarter(startOfReportingNewProject) };
	// Always set starting quarter = 1 for new projects BFINFOR-2020
	return { year: getYear(startOfReportingNewProject) - 1, quarter: 1 };
}

/**
 * Returns the interval we are working on
 */
export function getReportingQuarters(
	reportStatuses: ProjectQuarterlyDataAttribute[],
	today: Date,
	projectDTO: ProjectDTO
): YearQuarterInterval {
	const currentQuarter = getQuarter(today);
	const currentYear = getYear(today);
	const latestSubmittedOrApproved = getLatestReportStatus(reportStatuses, (attr) => {
		return attr.value === 'APPROVED' || attr.value === 'SUBMITTED';
	});
	const returnedQuarters: YearAndQuarter[] = reportStatuses
		.filter((status) => status.value === 'RETURNED')
		.map((status) => {
			return { year: status.year, quarter: status.quarter };
		});

	const earliesYearReturned = Math.min(
		..._(returnedQuarters)
			.map((returned) => returned.year)
			.uniq()
			.value()
	);
	const quartersForEarliestReturnedYear = returnedQuarters
		.filter((quarterAndYear) => quarterAndYear.year === earliesYearReturned)
		.map((quarterAndYear) => quarterAndYear.quarter);

	// start: earliest quarter where status does not exist or is REJECTED
	// can also be formulated as: the quarter after the latest status with SUBMITTED or APPROVED, defaulting to 1
	const start = latestSubmittedOrApproved ? addQuarter(latestSubmittedOrApproved) : getNewProjectQuarters(projectDTO);
	const startingQuarter = start.quarter;

	// end: latest quarter after starting quarter, before the current quarter
	const activeQuarters: number[] = [];
	let year = start.year;
	if (quartersForEarliestReturnedYear.length > 0) {
		quartersForEarliestReturnedYear.forEach((quarter) => activeQuarters.push(quarter));
		year = earliesYearReturned;
	} else if (start.year < currentYear) {
		for (let i = startingQuarter; i <= 4; i++) {
			activeQuarters.push(i);
		}
	} else if (start.year === currentYear) {
		for (let i = startingQuarter; i < currentQuarter; i++) {
			activeQuarters.push(i);
		}
	}

	return {
		year,
		quarters: activeQuarters,
	};
}

type YearQuarterInterval = {
	year: number;
	quarters: number[];
};

export function quarterNames(quarters: number[]) {
	return quarters.map((n) => `Q${n}`);
}

export function getYearAndQuartersForApproval(reportStatuses: ProjectQuarterlyDataAttribute[]): YearQuarterInterval {
	const years = _(reportStatuses)
		.filter((attr) => attr.value === 'SUBMITTED')
		.map((attr) => attr.year)
		.uniq()
		.value();

	const year = Math.min(...years);

	const quarters = reportStatuses
		.filter((reportStatus) => reportStatus.value === 'SUBMITTED' && reportStatus.year === year)
		.map((reportStatus) => reportStatus.quarter)
		.sort();

	return {
		year,
		quarters,
	};
}

export function getLastEditTime(projectStatus: ProjectQuarterlyDataAttribute[]): string {
	const dates: number[] = [];
	projectStatus.forEach((attribute) => {
		dates.push(new Date(attribute!.lastModified!).getTime());
	});

	return formatDateTime(Math.max(...dates));
}

/**
 * BFINFOR-119:
 * A project is qualified for submitting quarterly reports if it is not ended, or if it is ended, but some reporting remains.
 * All projects ended before REPORTING_INTERVAL_EARLIEST_CUTOFF is considered old and not qualified.
 * All projects without status is also considered old and not qualified.
 *
 * @param projectDTO the project to qualify
 * @param reportStatuses a list of all reportStatuses for the given project (may be empty)
 * @returns true if qualified
 */
export function isProjectQualifiedForReporting(
	projectDTO: ProjectDTO,
	reportStatuses: ProjectQuarterlyDataAttribute[]
): boolean {
	const projectStatus = getAttributeValue(projectDTO, 'STATUS') as ProjectStatus;

	// if a project is missing project status, it is likely very old and has ended a long time ago
	// This check can be removed once BFINFOR-174 is completed
	if (!projectStatus) {
		return false;
	}

	// if a project has not ended, it is qualified
	if (projectStatus !== 'ENDED') {
		return true;
	}

	const endDateStr = getAttributeValue(projectDTO, 'DATE_COLLECTION_END');
	const endDate = parseISO(endDateStr ?? '');
	const endYear = endDate.getFullYear();
	const endQuarter = getQuarter(endDate);

	//Dynamic cut off for ended projects, will only display the projects that ended within the last three years
	const ENDED_PROJECTS_CUTOFF: YearAndQuarter = { year: getYear(new Date()) - 2, quarter: getQuarter(new Date()) };

	// projects that ended before EARLIEST_CUTOFF will not be available for reporting
	if (isAfter(ENDED_PROJECTS_CUTOFF, { year: endYear, quarter: endQuarter })) {
		return false;
	}

	const statusForLastQuarter = reportStatuses.filter((s) => s.year === endYear && s.quarter === endQuarter);
	const hasApprovedFinalQuarter = statusForLastQuarter.length > 0 && statusForLastQuarter[0].value === 'APPROVED';

	// if there is an approved report for the last open quarter, the project is not qualified
	return !hasApprovedFinalQuarter;
}

export function useGetNextDeadline() {
	const quarterlyDeadlines = useQuarterlyDeadlines();
	return function getNextDeadline(lastSubmitted: ProjectQuarterlyDataAttribute) {
		if (!lastSubmitted) {
			return '';
		} else if (lastSubmitted.quarter === 4) {
			return `${quarterlyDeadlines[0].deadline}.${lastSubmitted.year + 1}`;
		} else if (lastSubmitted.quarter === 3) {
			return `${quarterlyDeadlines[lastSubmitted.quarter].deadline}.${lastSubmitted.year + 1}`;
		} else {
			return `${quarterlyDeadlines[lastSubmitted.quarter].deadline}.${lastSubmitted.year}`;
		}
	};
}

export const quarterlyReportAttributes: ProjectPeriodicDataAttributeType[] = [
	'REVENUE_TOLL',
	'EXPENSES_OPERATIONAL_EXCLUDING_DEPRECIATION',
	'FINANCE_REVENUE',
	'FINANCE_EXPENSES',
	'FINANCE_SUM_LOAN_PORTFOLIO_EOP',
	'FINANCE_LOAN_WEIGHTED_EFFECTIVE_INTEREST_RATE_EOP',
	'FINANCE_LOAN_LEVEL_SECURITY',
	'FINANCE_LOAN_EFFECTIVE_INTEREST_RATE',
	'FINANCE_SUM_DEPOSITS_EOP',
	'FINANCE_DEPOSITS_WEIGHTED_EFFECTIVE_INTEREST_RATE_EOP',
];

export const quarterlyReportAttributesWithFerryCrossings: ProjectPeriodicDataAttributeType[] = [
	...quarterlyReportAttributes,
	'TRAFFIC_PASSES_FERRY_RATE_GROUP_1',
	'TRAFFIC_PASSES_FERRY_RATE_GROUP_2',
	'TRAFFIC_PASSES_ZERO_EMISSION_FERRY_RATE_GROUP_1',
	'TRAFFIC_PASSES_ZERO_EMISSION_FERRY_RATE_GROUP_2',
];
